import {
  integer,
  maxLength,
  minLength,
  required,
  requiredIf,
} from '@vuelidate/validators';
import { computed } from 'vue';
import useVuelidate from '@vuelidate/core';
import type { FlowOperatorMetadata, ValidationResult, Period, PickerData } from '@/types/flow-types';
import { TypeCampaignEnum } from '@/types';
import { includesValuesValidator, minNavigationCriterionValidator, requiredWithMessage } from '@/helpers/CustomValidator';
import Form from './Form.vue';

export interface NavigationProductData {
  litteral_title?: {
    minimum_product_nbr?: string;
    product_picker_product?: string;
  };
  minimum_product_nbr?: number;
  product_picker_product?: {
    selection: PickerData;
  };
  operatorElementNav?: string;
}

export interface NavigationCategoryData {
  litteral_title?: {
    minimum_category_nbr?: string;
    product_picker_category?: string;
  };
  minimum_category_nbr?: number;
  product_picker_category?: {
    selection: PickerData;
  };
  operatorElementNav?: string;
}

export interface NavigationManufacturerData {
  litteral_title?: {
    minimum_manufacturer_nbr?: string;
    product_picker_manufacturer?: string;
  };
  minimum_manufacturer_nbr?: number;
  product_picker_manufacturer?: {
    selection: PickerData;
  };
  operatorElementNav?: string;
}

export interface NavigationUrl {
  url: string;
}

export interface NavigationUrlData {
  lists?: NavigationUrl[];
  operator?: string;
}

export interface NavigationHistoryFilters {
  products: NavigationProductData;
  categories: NavigationCategoryData;
  manufacturers: NavigationManufacturerData;
  urls: NavigationUrlData;
}

export interface NavigationHistoryData {
  custom_title: string;
  custom_description: string;
  outputs_number: number;
  initial_number_outputs?: null | number;
  products: NavigationProductData;
  categories: NavigationCategoryData;
  manufacturers: NavigationManufacturerData;
  urls: NavigationUrlData;
  operatorBoxNav: string;
  period_concerned: string;
  period_time: Period;
}

export const createDefaultData = (overrides: Partial<NavigationHistoryData> = {}): NavigationHistoryData => ({
  custom_title: '',
  custom_description: '',
  outputs_number: 1,
  products: {},
  categories: {},
  manufacturers: {},
  urls: {},
  operatorBoxNav: 'and',
  period_concerned: '0',
  period_time: {
    value: 1,
    unit: 'MINUTE',
  },
  ...overrides,
});

const NavigationHistory: FlowOperatorMetadata<NavigationHistoryData> = {
  id: 'boxnavigationtrigger',
  kind: 'filter',
  label: 'automatedScenarios.filters.navigationTrigger',
  icon: 'fa-mouse-pointer',
  hasConfiguration: true,
  // Deprecated: replaced by ContactSegmentation ("Conditions logiques").
  // Metadata kept registered so existing flows still render and validate.
  availableIn: [],

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: ['boxabandonpanier', 'boxperiodiclaunching', 'boxvisitpage'],
    },
    [TypeCampaignEnum.BULK]: {
      nextOperators: ['boxchoosebulklist', 'boxaudiencesetup'],
    },
  },

  Form,

  defaultData: createDefaultData,

  outputs: (data: NavigationHistoryData) => data.outputs_number,

  getDisplayLabel(data: NavigationHistoryData): string | null {
    return data.custom_title || null;
  },

  getSubtitle(data: NavigationHistoryData): string | null {
    return data.custom_description || null;
  },

  async validate(data: NavigationHistoryData): Promise<ValidationResult> {
    const normalizedData = {
      ...data,
      outputs_number: Number(data.outputs_number),
    };

    const rules = computed(() => ({
      custom_title: {
        requiredWithMessage: requiredWithMessage(),
        minLength: minLength(3),
        maxLength: maxLength(45),
      },
      outputs_number: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator([1, 2]),
      },
      period_concerned: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator(['0', '1']),
      },
      period_time: {
        value: {
          required: requiredIf(() => data.period_concerned === '1'),
          integer,
        },
        unit: {
          required: requiredIf(() => data.period_concerned === '1'),
          includesValuesValidator: includesValuesValidator(['SECOND', 'MINUTE', 'HOUR', 'DAY']),
        },
      },
      operatorBoxNav: {
        required,
      },
      products: {
        minNavigationCriterionValidator: minNavigationCriterionValidator(
          'filter',
          data.products,
          data.categories,
          data.manufacturers,
          data.urls,
        ),
      },
    }));

    const v$ = useVuelidate(rules, normalizedData);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$,
    };
  },

  beforeInsert(data: NavigationHistoryData): NavigationHistoryData {
    const dataToReturn = { ...data };
    if (typeof dataToReturn.initial_number_outputs !== 'undefined') {
      delete dataToReturn.initial_number_outputs;
    }
    return dataToReturn;
  },
};

export default NavigationHistory;
